/**
 * Discounts v2 stacking engine — computes the deterministic aggregate impact
 * of an ordered stack of discount elements on a draft sales-order line.
 *
 * Pure and I/O-free so backend (facts stamping, submission freeze) and
 * frontend (stack-builder price preview) run byte-identical math.
 *
 * Sequential compounding, in `applicationOrder`:
 *   1. FIXED_PRICE (at most one, must be first) sets the running price.
 *   2. PERCENT_BPS_OFF multiplies the running price down by its bps and
 *      AMOUNT_OFF subtracts from it — in whatever order the stack says
 *      (the order is user-controlled and changes the math).
 *
 * No intermediate rounding — the running price keeps full precision and is
 * rounded once at the end (banker's rounding, 2dp). Trace entries are rounded
 * for display only.
 */

import Decimal from "decimal.js";
import {
  DiscountElementType,
  type DiscountStackElementInput,
  type DiscountStackResult,
  type DiscountStackTraceEntry,
} from "@erp/shared-types";
import { parseMoney, serializeMoney, type MoneyInput } from "./money";

const BPS_DENOMINATOR = 10_000;

/** Why a stack failed validation; `elementId` set when one element is at fault. */
export type DiscountStackValidationCode =
  | "EMPTY_STACK"
  | "DUPLICATE_APPLICATION_ORDER"
  | "MULTIPLE_FIXED_PRICE"
  | "FIXED_PRICE_NOT_FIRST"
  | "INVALID_BPS"
  | "NEGATIVE_VALUE"
  | "NEGATIVE_RUNNING_PRICE"
  | "INVALID_LIST_PRICE";

export class DiscountStackValidationError extends Error {
  readonly code: DiscountStackValidationCode;
  readonly elementId?: string;

  constructor(
    code: DiscountStackValidationCode,
    message: string,
    elementId?: string,
  ) {
    super(message);
    this.name = "DiscountStackValidationError";
    this.code = code;
    this.elementId = elementId;
  }
}

export interface ComputeDiscountStackInput {
  /** The undiscounted (list) unit price the stack applies against. */
  listUnitPrice: MoneyInput;
  /** Line quantity, for the total-discount figure. */
  quantity: MoneyInput;
  /** The stack; order is taken from `applicationOrder`, not array position. */
  elements: DiscountStackElementInput[];
}

function validateStack(sorted: DiscountStackElementInput[]): void {
  if (sorted.length === 0) {
    throw new DiscountStackValidationError(
      "EMPTY_STACK",
      "A discount stack must contain at least one element",
    );
  }

  const seenOrders = new Set<number>();
  for (const el of sorted) {
    if (seenOrders.has(el.applicationOrder)) {
      throw new DiscountStackValidationError(
        "DUPLICATE_APPLICATION_ORDER",
        `Duplicate applicationOrder ${el.applicationOrder}`,
        el.id,
      );
    }
    seenOrders.add(el.applicationOrder);
  }

  const fixedPriceElements = sorted.filter(
    (el) => el.discountType === DiscountElementType.FIXED_PRICE,
  );
  if (fixedPriceElements.length > 1) {
    throw new DiscountStackValidationError(
      "MULTIPLE_FIXED_PRICE",
      "A stack may contain at most one FIXED_PRICE element",
      fixedPriceElements[1]?.id,
    );
  }
  if (fixedPriceElements.length === 1 && sorted[0] !== fixedPriceElements[0]) {
    throw new DiscountStackValidationError(
      "FIXED_PRICE_NOT_FIRST",
      "A FIXED_PRICE element must be first in the stack",
      fixedPriceElements[0]?.id,
    );
  }

  for (const el of sorted) {
    const value = parseMoney(el.discountValue);
    if (value.isNegative()) {
      throw new DiscountStackValidationError(
        "NEGATIVE_VALUE",
        `discountValue must be ≥ 0 (got ${value.toString()})`,
        el.id,
      );
    }
    if (el.discountType === DiscountElementType.PERCENT_BPS_OFF) {
      const isIntegral = value.isInteger();
      if (!isIntegral || value.lte(0) || value.gt(BPS_DENOMINATOR)) {
        throw new DiscountStackValidationError(
          "INVALID_BPS",
          `PERCENT_BPS_OFF requires an integer bps in (0, ${BPS_DENOMINATOR}] (got ${value.toString()})`,
          el.id,
        );
      }
    }
  }
}

/**
 * Compute the final unit price and total discount of an ordered element stack.
 * Throws {@link DiscountStackValidationError} on an invalid stack, including
 * when the running price would go negative — reject, don't clamp, so the UI
 * blocks the selection instead of silently charging zero.
 */
export function computeDiscountStack(
  input: ComputeDiscountStackInput,
): DiscountStackResult {
  const listUnitPrice = parseMoney(input.listUnitPrice);
  if (listUnitPrice.isNegative()) {
    throw new DiscountStackValidationError(
      "INVALID_LIST_PRICE",
      `listUnitPrice must be ≥ 0 (got ${listUnitPrice.toString()})`,
    );
  }
  const quantity = parseMoney(input.quantity);

  const sorted = [...input.elements].sort(
    (a, b) => a.applicationOrder - b.applicationOrder,
  );
  validateStack(sorted);

  const trace: DiscountStackTraceEntry[] = [];
  let runningPrice = listUnitPrice;

  for (const el of sorted) {
    const value = parseMoney(el.discountValue);
    const before = runningPrice;

    switch (el.discountType) {
      case DiscountElementType.FIXED_PRICE:
        runningPrice = value;
        break;
      case DiscountElementType.PERCENT_BPS_OFF:
        runningPrice = runningPrice.times(
          new Decimal(1).minus(value.div(BPS_DENOMINATOR)),
        );
        break;
      case DiscountElementType.AMOUNT_OFF:
        runningPrice = runningPrice.minus(value);
        break;
    }

    if (runningPrice.isNegative()) {
      throw new DiscountStackValidationError(
        "NEGATIVE_RUNNING_PRICE",
        `Stack drives the unit price below zero at element ${el.id}`,
        el.id,
      );
    }

    trace.push({
      elementId: el.id,
      applicationOrder: el.applicationOrder,
      discountType: el.discountType,
      discountValue: value.toString(),
      unitPriceBefore: serializeMoney(before),
      unitPriceAfter: serializeMoney(runningPrice),
      discountAmount: serializeMoney(before.minus(runningPrice)),
    });
  }

  const perUnitDiscountUnrounded = listUnitPrice.minus(runningPrice);
  return {
    finalUnitPrice: serializeMoney(runningPrice),
    perUnitDiscount: serializeMoney(perUnitDiscountUnrounded),
    totalDiscountAmount: serializeMoney(
      perUnitDiscountUnrounded.times(quantity),
    ),
    trace,
  };
}

/**
 * Ordering for a set of elements about to become a stack: the caller's order
 * is preserved (it's user-controlled and changes the math), except FIXED_PRICE
 * (at most one) is hoisted first — it overwrites the running price, so placed
 * anywhere else it would erase the steps before it. Returns new inputs with
 * applicationOrder assigned 0..n-1.
 */
export function orderElementsForStack(
  elements: Array<Omit<DiscountStackElementInput, "applicationOrder">>,
): DiscountStackElementInput[] {
  const isFixed = (el: Omit<DiscountStackElementInput, "applicationOrder">) =>
    el.discountType === DiscountElementType.FIXED_PRICE;
  return [
    ...elements.filter(isFixed),
    ...elements.filter((el) => !isFixed(el)),
  ].map((el, applicationOrder) => ({ ...el, applicationOrder }));
}
